import { useEffect, useMemo, useState } from 'react';
import { useUserIdentity } from '@lucid/portal-core';
import { Box, Button, DatePicker, Dropdown, Input, RadioGroup, Typography } from '@lucid/lego';
import type { DatePickerProps } from '@lucid/lego';
import { useGetTestDriveVehicles } from '../../../../api/services/lupp/test-drive/test-drive';
import { formatVehicleModelName } from '../../../../utils/tools';
import {
  getBookingDatePickerValue,
  getBookingDateType,
  getBookingStatusOptions,
  getTestDriveTypeLabel,
  toBookingDateInstant,
  withBookingDateType,
} from '../utils';
import type { BookingDateType } from '../utils';
import type { BookingFilters } from '../hooks/useBookingSearchTable';
import styles from './FilterPanel.module.scss';

type FilterPanelProps = {
  theme: 'light' | 'dark';
  locale?: string;
  labels: Record<string, string>;
  filters: BookingFilters;
  onApply: (filters: BookingFilters) => void;
  onClear: () => void;
};

/**
 * Content rendered inside the Table's slide-out "Filters" panel. Holds a local pending copy of
 * the filters so changes are only sent to the server search when "Apply" is clicked, matching
 * the conventional filter-panel pattern used elsewhere (e.g. Fleet Health Monitoring Alerts).
 *
 * @component
 * @returns {JSX.Element} The booking filter panel content
 */
const FilterPanel: React.FC<FilterPanelProps> = ({
  theme,
  locale = 'en-US',
  labels,
  filters,
  onApply,
  onClear,
}) => {
  const [pending, setPending] = useState<BookingFilters>(() =>
    withBookingDateType(filters, getBookingDateType(filters))
  );
  const [dateType, setDateType] = useState<BookingDateType>(() => getBookingDateType(filters));
  const fromKey = dateType === 'start' ? 'appointmentStartDate' : 'appointmentEndFrom';
  const toKey = dateType === 'start' ? 'appointmentEndDate' : 'appointmentEndTo';
  const invalidDateRange = Boolean(
    pending[fromKey] && pending[toKey] && new Date(pending[fromKey]) > new Date(pending[toKey])
  );

  // Re-sync when filters are reset/changed externally (e.g. "Clear Filters").
  useEffect(() => {
    const type = getBookingDateType(filters);
    setDateType(type);
    setPending(withBookingDateType(filters, type));
  }, [filters]);

  const { userIdentity } = useUserIdentity();
  const { data: testDriveVehicles } = useGetTestDriveVehicles({
    agencyId: userIdentity?.businessOrganizationId ?? '',
  });

  // Dropdown of the models this organization actually allows for test drives, sourced from the
  // same agency vehicle config used when scheduling a test drive, so selected values always
  // match the raw `vehicleModel` the backend searches on (distinct from `table.vehicle`, which
  // renders model+trim together for display).
  const vehicleModelOptions = useMemo(() => {
    const uniqueModels = Array.from(
      new Set((testDriveVehicles ?? []).map((vehicle) => vehicle.model).filter(Boolean))
    );
    return uniqueModels.map((model) => ({ label: formatVehicleModelName(model), value: model }));
  }, [testDriveVehicles]);

  const statusOptions = useMemo(() => getBookingStatusOptions(labels), [labels]);
  const testDriveTypeOptions = useMemo(
    () => [
      { label: getTestDriveTypeLabel('STANDARD', labels), value: 'STANDARD' },
      { label: getTestDriveTypeLabel('EXTENDED', labels), value: 'EXTENDED' },
    ],
    [labels]
  );

  return (
    <Box className={styles.panel}>
      <Box className={styles.field}>
        <Typography variant="label" size="small" color="gray-5" theme={theme}>
          {labels?.['table.testDriveId'] ?? 'Test Drive ID'}
        </Typography>
        <Input
          name="booking-test-drive-id-filter"
          theme={theme}
          size="medium"
          placeholder={labels?.['table.testDriveId'] ?? 'Test Drive ID'}
          value={pending.testDriveId ?? ''}
          onChange={(event) =>
            setPending({ ...pending, testDriveId: event.target.value || undefined })
          }
          dataTestId="booking-test-drive-id-filter"
        />
      </Box>
      <Box className={styles.field}>
        <Typography variant="label" size="small" color="gray-5" theme={theme}>
          {labels?.['table.status'] ?? 'Status'}
        </Typography>
        <Dropdown
          theme={theme}
          size="medium"
          name="booking-status-filter"
          placeholder={labels?.['table.status'] ?? 'Status'}
          options={statusOptions}
          selectedOption={pending.status}
          handleSelect={(value) => setPending({ ...pending, status: value || undefined })}
          showValidation={false}
        />
      </Box>
      <Box className={styles.field}>
        <Typography variant="label" size="small" color="gray-5" theme={theme}>
          {labels?.['table.vehicleModel'] ?? 'Vehicle model'}
        </Typography>
        <Dropdown
          theme={theme}
          size="medium"
          name="booking-vehicle-model-filter"
          placeholder={labels?.['table.vehicleModel'] ?? 'Vehicle model'}
          options={vehicleModelOptions}
          selectedOption={pending.vehicleModel}
          handleSelect={(value) => setPending({ ...pending, vehicleModel: value || undefined })}
          showValidation={false}
        />
      </Box>
      <Box className={styles.field}>
        <Typography variant="label" size="small" color="gray-5" theme={theme}>
          {labels?.['table.vin'] ?? 'VIN'}
        </Typography>
        <Input
          name="booking-vin-filter"
          theme={theme}
          size="medium"
          placeholder={labels?.['table.vin'] ?? 'VIN'}
          value={pending.vin ?? ''}
          onChange={(event) => setPending({ ...pending, vin: event.target.value || undefined })}
          dataTestId="booking-vin-filter"
        />
      </Box>
      <Box className={styles.field}>
        <Typography variant="label" size="small" color="gray-5" theme={theme}>
          {labels?.['table.testDriveType'] ?? 'Test Drive Type'}
        </Typography>
        <Dropdown
          theme={theme}
          size="medium"
          name="booking-test-drive-type-filter"
          placeholder={labels?.['table.testDriveType'] ?? 'Test Drive Type'}
          options={testDriveTypeOptions}
          selectedOption={pending.testDriveType}
          handleSelect={(value) => setPending({ ...pending, testDriveType: value || undefined })}
          showValidation={false}
        />
      </Box>
      <Box className={styles.field}>
        <Typography variant="label" size="small" color="gray-5" theme={theme}>
          {labels?.['table.location'] ?? 'Location'}
        </Typography>
        <Input
          name="booking-location-filter"
          theme={theme}
          size="medium"
          placeholder={labels?.['table.location'] ?? 'Location'}
          value={pending.locationName ?? ''}
          onChange={(event) =>
            setPending({ ...pending, locationName: event.target.value || undefined })
          }
          dataTestId="booking-location-filter"
        />
      </Box>
      <Box className={styles.dateFilter}>
        <Typography variant="label" size="medium" color="gray-5" theme={theme}>
          {labels?.['filter.dateFilter'] ?? 'Date Filter'}
        </Typography>
        <RadioGroup
          name="booking-date-type-filter"
          theme={theme}
          label={labels?.['filter.dateType'] ?? 'Date Type'}
          selectedValue={dateType}
          options={[
            {
              label: labels?.['filter.appointmentStartDate'] ?? 'Appointment Start Date',
              value: 'start',
            },
            {
              label: labels?.['filter.appointmentEndDate'] ?? 'Appointment End Date',
              value: 'end',
            },
          ]}
          handleChange={({ value }) => {
            if (value === 'start' || value === 'end') {
              setPending((prev) => withBookingDateType(prev, value));
              setDateType(value);
            }
          }}
          showValidation={false}
          dataTestId="booking-date-type-filter"
          gaComponent="test-drive-bookings-date-type"
        />
        <Typography variant="body" size="small" color="gray-5" theme={theme}>
          {dateType === 'start'
            ? (labels?.['filter.startDateRangeHelp'] ??
              'Show appointments starting within this date range.')
            : (labels?.['filter.endDateRangeHelp'] ??
              'Show appointments ending within this date range.')}
        </Typography>
        <Box className={styles.dateRange}>
          <Box className={styles.field}>
            <DatePicker
              name="booking-date-from-filter"
              theme={theme}
              locale={locale as DatePickerProps['locale']}
              value={getBookingDatePickerValue(pending[fromKey])}
              onChange={(date: Date) =>
                setPending((prev) => ({
                  ...prev,
                  [fromKey]: toBookingDateInstant(date.toISOString().slice(0, 10), 'start'),
                }))
              }
              onClear={() => setPending((prev) => ({ ...prev, [fromKey]: undefined }))}
              label={labels?.['filter.fromDate'] ?? 'From Date'}
            />
          </Box>
          <Box className={styles.field}>
            <DatePicker
              name="booking-date-to-filter"
              theme={theme}
              locale={locale as DatePickerProps['locale']}
              value={getBookingDatePickerValue(pending[toKey])}
              onChange={(date: Date) =>
                setPending((prev) => ({
                  ...prev,
                  [toKey]: toBookingDateInstant(date.toISOString().slice(0, 10), 'end'),
                }))
              }
              onClear={() => setPending((prev) => ({ ...prev, [toKey]: undefined }))}
              label={labels?.['filter.toDate'] ?? 'To Date'}
              validationResponse={
                invalidDateRange
                  ? {
                      validationType: 'error',
                      message:
                        labels?.['filter.invalidDateRange'] ??
                        'To Date must be on or after From Date.',
                    }
                  : undefined
              }
            />
          </Box>
        </Box>
      </Box>
      <Box className={styles.actions}>
        <Button
          type="tertiary"
          color="neutral"
          size="small"
          theme={theme}
          label={labels?.['table.clearFilters'] ?? 'Clear Filters'}
          handleClick={() => {
            setPending({});
            setDateType('start');
            onClear();
          }}
          gaComponent="test-drive-bookings-clear-filters"
          dataTestId="clear-filters-button"
        />
        <Button
          type="primary"
          color="neutral"
          size="small"
          theme={theme}
          label={labels?.['table.apply'] ?? 'Apply'}
          disabled={invalidDateRange}
          handleClick={() => {
            if (!invalidDateRange) onApply(pending);
          }}
          gaComponent="test-drive-bookings-apply-filters"
          dataTestId="apply-filters-button"
        />
      </Box>
    </Box>
  );
};

export default FilterPanel;
